<template>
    <v-app id="app" v-if="experienceLoaded" :style="cssVars" :class="{ 'swipper-no-swipping': showModal }" :lang="$i18n.locale">
        <!-- Skip Navigation Link -->
        <SkipNavigation />

        <!-- Screen Reader Announcer -->
        <output id="screen-reader-announcer" class="sr-only" aria-live="polite" aria-atomic="true"></output>

        <v-app-bar v-if="show"
                   app
                   flat
                   color="headerColor"
                   :min-height="isMobile ? '56px' : '64px'"
                   role="banner">
            <a href="/" :aria-label="$t('home')">
                <v-img class="mx-4"
                       :src="`./assets/Branding/${this.experience.key}/Images/${getLogo}`"
                       :max-width="appBarLogoMaxWidth"
                       :max-height="isMobile ? ($experience.appBarMobileLogoMaxHeight ? $experience.appBarMobileLogoMaxHeight : '18px') : ($experience.appBarLogoMaxHeight ? $experience.appBarLogoMaxHeight : '50px')"
                       :alt="$t('app.logoAlt', 'Vault Logo')"
                       contain></v-img>
            </a>

            <v-spacer></v-spacer>
            <v-icon v-if="isMobile"
                    @click.stop="drawer = !drawer"
                    dark
                    :aria-label="drawer ? $t('accessibility.closeMenu') : $t('accessibility.openMenu')"
                    role="button"
                    tabindex="0"
                    @keydown.enter="drawer = !drawer"
                    @keydown.space.prevent="drawer = !drawer">
                {{drawer ? 'mdi-close' : 'mdi-menu'}}
            </v-icon>

            <nav v-if="!isMobile" role="navigation" :aria-label="$t('accessibility.navigation')">
                <v-btn class="text-none btn-nav text-normal"
                       v-bind:class="[currentMenu == 'home' ? 'btn-nav-active' : '']"
                       :text="currentMenu != 'home'"
                       :dark="currentMenu != 'home'"
                       depressed
                       plain
                       to="/"
                       :aria-current="currentMenu == 'home' ? 'page' : null">
                    <v-icon class="mr-2" aria-hidden="true">mdi-home-outline</v-icon>
                    {{formatMenuText($t('home'))}}
                </v-btn>
                <v-btn v-if="show && user.menus?.includes('userManagement')"
                       class="text-none btn-nav text-normal"
                       v-bind:class="[currentMenu == 'userManagement' ? 'btn-nav-active' : '']"
                       :text="currentMenu != 'userManagement'"
                       :dark="currentMenu != 'userManagement'"
                       depressed
                       plain
                       to="/userManagement"
                       :aria-current="currentMenu == 'userManagement' ? 'page' : null">
                    <v-icon class="mr-2" aria-hidden="true">mdi-account-group-outline</v-icon>
                    {{formatMenuText($t('app.manage-users'))}}
                </v-btn>
                <v-btn v-if="show && user.menus?.includes('reports')"
                       class="text-none btn-nav text-normal"
                       v-bind:class="[currentMenu == 'reports' ? 'btn-nav-active' : '']"
                       :text="currentMenu != 'reports'"
                       :dark="currentMenu != 'reports'"
                       depressed
                       plain
                       to="/reports"
                       :aria-current="currentMenu == 'reports' ? 'page' : null">
                    <v-icon class="mr-2" aria-hidden="true">mdi-chart-bar</v-icon>
                    {{formatMenuText($t('reports.menu'))}}
                </v-btn>
                <v-btn class="text-none btn-nav text-normal"
                       v-bind:class="[currentMenu == 'faq' ? 'btn-nav-active' : '']"
                       :text="currentMenu != 'faq'"
                       :dark="currentMenu != 'faq'"
                       depressed
                       plain
                       to="/faq"
                       :aria-current="currentMenu == 'faq' ? 'page' : null">
                    <v-icon class="mr-2" aria-hidden="true">mdi-help</v-icon>
                    {{formatMenuText($t('app.faq'))}}
                </v-btn>
            </nav>
            <v-menu v-if="!isMobile"
                    bottom offset-y>
                <template v-slot:activator="{ on, attrs, value }">
                    <v-icon v-if="!isMobile"
                            dark
                            class="no-background"
                            v-bind="attrs"
                            v-on="on"
                            aria-hidden="true"
                            tabindex="-1">
                        mdi-menu-down
                    </v-icon>
                    <v-btn v-if="!isMobile"
                           icon outlined
                           class="text-normal text-uppercase font-weight-bold ml-4"
                           v-bind:class="[value ? 'btn-avatar-active' : 'btn-avatar']"
                           :plain="value"
                           v-bind="attrs"
                           v-on="on"
                           :aria-label="$t('settings')"
                           :aria-expanded="value ? 'true' : 'false'"
                           aria-haspopup="true">
                        {{initials}}
                    </v-btn>

                </template>
                <v-list dense nav role="menu">
                    <v-list-item @click="toggleLocale()" role="menuitem">
                        <v-list-item-content class="py-0">
                            <v-list-item-title class="text-color-primary text-normal font-weight-bold line-height-normal" :aria-label="$t('accessibility.toggleLanguage')">{{$t('locale')}}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="ProfileModal()" role="menuitem">
                        <v-list-item-icon class="mr-1">
                            <v-icon color="primary" aria-hidden="true">mdi-cog-outline</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-0">
                            <v-list-item-title class="text-color-primary text-normal font-weight-bold line-height-normal">{{$t('settings')}}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="showLogout = !showLogout">
                        <v-list-item-icon class="mr-1">
                            <v-icon color="primary">mdi-logout</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content class="py-0">
                            <v-list-item-title class="text-color-primary text-normal font-weight-bold line-height-normal">{{$t('logout')}}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-menu>

        </v-app-bar>
        <div v-if="getSecondLogo" class="ml-4" style="margin-top: 64px; position: absolute;">
            <v-img class=""
                   :src="`../../assets/Branding/${this.$experience.key}/Images/${getSecondLogo}`"
                   :max-width="logoSecondLoginMaxWidth ? logoSecondLoginMaxWidth : '270px'"
                   :max-height="logoSecondLoginMaxheight ? logoSecondLoginMaxheight : null"
                   contain>
            </v-img>
        </div>
        <v-navigation-drawer v-if="isMobile && show"
                             v-model="drawer"
                             color="primary"
                             app
                             dark
                             right
                             width="100%"
                             role="navigation"
                             :aria-label="$t('accessibility.navigation')">
            <v-row class="ma-0 pa-0" align="center" justify="end">
                <v-icon @click.stop="drawer = !drawer"
                        dark
                        class="mt-5 mb-3 mr-3"
                        :aria-label="$t('accessibility.closeMenu')"
                        role="button"
                        tabindex="0"
                        @keydown.enter="drawer = !drawer"
                        @keydown.space.prevent="drawer = !drawer">
                    mdi-close
                </v-icon>
            </v-row>
            <v-list>
                <v-list-item-group v-model="menuMobile" role="navigation" :aria-label="$t('accessibility.navigation')">
                    <v-row align="center" justify="center" class="ma-4">
                        <div class="circle"></div>
                        <span class="text-large white--text font-weight-medium ma-1" role="heading" aria-level="2">{{this.status.loggedIn ? this.user.firstname.toUpperCase() + ' ' + this.user.lastname.toUpperCase() : ''}}</span>
                    </v-row>

                    <v-divider></v-divider>
                    <v-list-item to="/" :aria-current="currentMenu == 'home' ? 'page' : null">
                        <v-list-item-title class="text-large text-center font-weight-medium">{{formatMenuText($t('home'))}}</v-list-item-title>
                    </v-list-item>
                    <!--
                                        <v-list-item v-if="show user.menus?.includes('userManagement')"
                                                     to="/userManagement">
                                            <v-list-item-title class="text-large text-center font-weight-medium">{{formatMenuText($t('app.manage-users'))}}</v-list-item-title>
                                        </v-list-item>
                -->
                    <v-list-item v-if="show && user.menus?.includes('reports')" to="/reports" :aria-current="currentMenu == 'reports' ? 'page' : null">
                        <v-list-item-title class="text-large text-center font-weight-medium">{{formatMenuText($t('reports.menu'))}}</v-list-item-title>
                    </v-list-item>

                    <v-list-item to="/faq" :aria-current="currentMenu == 'faq' ? 'page' : null">
                        <v-list-item-title class="text-large text-center font-weight-medium">{{formatMenuText($t('app.faq'))}}</v-list-item-title>
                    </v-list-item>

                    <v-divider></v-divider>

                    <v-list-item @click="toggleLocale()" inactive :aria-label="$t('accessibility.toggleLanguage')">
                        <v-list-item-title class="text-normal text-center font-weight-medium">{{formatMenuText($t('locale'))}}</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="ProfileModal()" inactive>
                        <v-row align="center" justify="center">
                            <div class="mr-1">
                                <v-icon color="white" aria-hidden="true">mdi-cog-outline</v-icon>
                            </div>
                            <div class="text-normal text-center font-weight-medium">{{formatMenuText($t('settings'))}}</div>
                        </v-row>
                    </v-list-item>

                    <v-list-item @click="showLogout = !showLogout" inactive>
                        <v-row align="center" justify="center">
                            <div class="mr-1">
                                <v-icon color="white" aria-hidden="true">mdi-logout</v-icon>
                            </div>
                            <div class="text-normal text-center font-weight-medium">{{formatMenuText($t('logout'))}}</div>
                        </v-row>
                    </v-list-item>
                </v-list-item-group>
            </v-list>
        </v-navigation-drawer>

        <v-main :class="{ 'pt-0': !show }" id="main-content" role="main" :aria-label="$t('accessibility.mainContent')">
            <!-- Breadcrumb Navigation for WCAG 2.0 Level AA (2.4.5 Multiple Ways) -->
            <v-container v-if="show" fluid :class="getSecondLogo ? 'px-5 pt-10 pb-0' : 'px-5 pt-4 pb-0'">
                <Breadcrumb />
            </v-container>

            <router-view :experience="experience" @setExperience="setExperience" @toggleLocale="toggleLocale"></router-view>
        </v-main>
        <v-dialog v-model="showModal"
                  :persistent="isProfileModal || $vuetify.breakpoint.mobile"
                  :fullscreen="$vuetify.breakpoint.mobile"
                  :scrollable="$vuetify.breakpoint.height >= 500"
                  max-width="500px">
            <Settings v-if="user != null"
                      :ShowUserInfo="ShowUserInfo"
                      :ShowPasswordManager="ShowPasswordManager"
                      :isProfileModal="isProfileModal"
                      :isLegalModal="isLegalModal"
                      :MustSetPassword="MustSetPassword"
                      :PasswordExpired="PasswordExpired"
                      :SecurityData="securityData"
                      :UserData="userData"
                      @userDataUpdated="userDataUpdated"
                      @close-modal="CloseModalfunction" />
        </v-dialog>
        <!--Logout Dialog-->
        <v-dialog v-model="showLogout" max-width="500px">
            <v-card class="pa-8 rounded-xl" flat>
                <v-row align="center" justify="end">
                    <v-hover v-slot="{hover}">
                        <v-btn :color="hover ? 'linkHover' : 'textColor'"
                               dark
                               text
                               plain
                               class="text-none text-large"
                               @click="showLogout = false;">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-hover>
                </v-row>
                <v-row align="center" justify="center">
                    <v-icon size="96px"
                            color="primary"
                            class="my-5">mdi-logout</v-icon>
                </v-row>
                <v-card-title class="d-block text-center card-title font-weight-bold">{{$t('app.logoutTitle')}}</v-card-title>
                <v-card-subtitle class="text-center card-subtitle font-weight-medium mt-1">{{$t('app.logoutSubtitle')}}</v-card-subtitle>
                <v-card-actions class="mt-4 mx-auto">
                    <v-row justify="space-around">
                        <v-hover v-slot="{hover}">
                            <v-btn :color="hover ? 'buttonHover' : 'primary'"
                                   dark
                                   depressed
                                   min-width="33%"
                                   class="text-none text-normal min-height-1"
                                   @click="onLogout">
                                {{$t('yes')}}
                            </v-btn>
                        </v-hover>
                        <v-hover v-slot="{hover}">
                            <v-btn :color="hover ? 'buttonHover' : 'primary'"
                                   dark
                                   depressed
                                   min-width="33%"
                                   class="text-none text-normal min-height-1"
                                   @click="showLogout = false">
                                {{$t('no')}}
                            </v-btn>
                        </v-hover>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-footer v-if="show"
                  padless
                  :height="isMobile ? 'auto' : '56px'">
            <v-row justify="center"
                   align="center"
                   class="footerColor text-normal opacity-1 py-4 text-center ma-0"
                   :class="{'flex-column': isMobile}">
                <div :class="{'d-flex align-center': !isMobile, 'mb-2': isMobile}">
                    <span>{{$t('app.poweredBy')}}</span>
                    <v-img class="mx-2"
                           :src="`./assets/Branding/Sogica/Images/${this.$experience.footerLogo}`"
                           max-width="125px"
                           max-height="25px"
                           contain></v-img>
                </div>
                <span class="mx-2" v-if="!isMobile">|</span>
                <a :href="$i18n.locale === 'fr' ? './PolitiqueEtConditions.pdf' : './PrivacyAndConditions.pdf'"
                   target="_blank"
                   class="footerColor text-decoration-none">
                    {{$t('app.privacyPolicy')}}
                </a>
            </v-row>
        </v-footer>

        <!-- Keyboard Shortcuts Help Dialog -->
        <KeyboardShortcutsHelp v-model="showKeyboardHelp" />
    </v-app>
</template>
<style scoped>
    .no-background {
        background-color: transparent !important;
    }
    .swipper-no-swipping {
        overflow-y: clip !important;
    }
</style>
<script>
    import Vue from "vue";
    import Cookies from 'js-cookie'
    import { localizationService, loginService } from "./services";
    import { mapState } from "vuex";
    import Settings from "./components/settings"
    import SkipNavigation from "./components/SkipNavigation"
    import KeyboardShortcutsHelp from "./components/KeyboardShortcutsHelp"
    import Breadcrumb from "./components/Breadcrumb"
    import brandingJson from "./assets/Branding/vuetify.branding.json"
    import { SHORTCUTS } from "./utils/keyboardShortcuts"

    export default {
        name: "App",
        components: { 
            Settings,
            SkipNavigation,
            KeyboardShortcutsHelp,
            Breadcrumb
        },
        // les donn�es initiales
        data() {
            return {
                isSmallDisplay: false,
                reportsInfos: [],
                selectedReportCodes: new Array(0),
                loaded: false,
                showKeyboardHelp: false,
                domLoaded: false,
                langFr: "fr",
                langEn: "en",
                showModal: false,
                ShowUserInfo: false,
                ShowPasswordManager: false,
                MustSetPassword: false,
                PasswordExpired: false,
                isLegalModal: false,
                isProfileModal: false,
                showOnBoardingModal: false,
                search: "",
                treatedReportsData: {},
                sortedAttribute: "",
                sortedWay: "asc",
                securityData: undefined,
                userData: undefined,
                showLogout: false,
                drawer: false,
                menuMobile: null,
                experienceLoaded: false,
            }
        },
        created() {
            // Register global keyboard shortcuts
            this.$nextTick(() => {
                this.registerKeyboardShortcuts();
            });
        },
        beforeMount() {
            this.setExperience();

            if (this.status.loggedIn) {
                if (!this.showModal)
                    this.initDashboard();
            }
        },

        watch: {
            'status.loggedIn': function (newVal, oldVal) {
                if (newVal === true && !oldVal) {
                    if (!this.showModal)
                        this.initDashboard();
                }
            }
        },
        computed: {
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },
            ...mapState("account", ["status", "user"]),
            experience() {
                return Vue.prototype.$experience;
            },
            filteredReports: function () {
                let self = this;

                if (self.reportsInfos === undefined) return [];

                return this.reportsInfos.filter(function (report) {
                    let reportTitle = report.Title.toLowerCase(),
                        searchTerm = self.search.toLowerCase();
                    if (reportTitle == null) { return false; }
                    return reportTitle.indexOf(searchTerm) > -1;
                });
            },
            fullname: function () {
                return `${this.user.lastname}, ${this.user.firstname}`;
            },
            initials: function () {
                return `${this.user.firstname.substr(0, 1)}${this.user.lastname.substr(0, 1)}`;
            },
            show() {
                return this.status.loggedIn;
            },
            cssVars() {
                return {
                    '--background-color': `${this.$vuetify.theme.themes.light.bodyBackgroundColor}`,
                    '--login-background-color': `${this.$vuetify.theme.themes.light.loginBackgroundColor}`,
                    '--primary': `${this.$vuetify.theme.themes.light.primary}`,
                    '--titleColor': `${this.$vuetify.theme.themes.light.titleColor}`,
                    '--rowHighlightColor': `${this.$vuetify.theme.themes.light.rowHighlightColor}`,
                    '--buttonHoverColor': `${this.$vuetify.theme.themes.light.buttonHover}`,
                    '--SVGFontColor': `${this.$vuetify.theme.themes.light.SVGFontColor}`,
                    '--tileColor': `${this.$vuetify.theme.themes.light.tileColor}`,
                    '--expansionPanelFontColor': `${this.$vuetify.theme.themes.light.expansionPanelFontColor}`,
                    '--loginHeaderBackgroundColor': `${this.$vuetify.theme.themes.light.loginHeaderBackgroundColor}`,
                    '--loginHeaderFontColor': `${this.$vuetify.theme.themes.light.loginHeaderFontColor}`,
                    '--advancedSearchHeaderColor': `${this.$vuetify.theme.themes.light.advancedSearchHeaderColor}`,
                    '--navHoverBackgroundColor': `${this.$experience.navHoverBackgroundColor || '#FFFFFF'}`,
                    '--navHoverFontColor': `${this.$experience.navHoverFontColor || this.$vuetify.theme.themes.light.primary}`,
                    '--headerTextColor': `${this.$vuetify.theme.themes.light.headerTextColor}`,
                    '--activeNavTextColor': `${this.$experience.activeNavTextColor || this.$vuetify.theme.themes.light.primary}`,
                }
            },
            currentMenu() {
                return this.$route.name;
            },
            appBarLogoMaxWidth() {
                return this.isMobile ? (this.$experience.appBarMobileLogoMaxWidth ? this.$experience.appBarMobileLogoMaxWidth : '200px')
                    : this.$vuetify.breakpoint.mdOnly ? (this.$experience.appBarLogoMaxWidth_md ? this.$experience.appBarLogoMaxWidth_md : (this.$experience.appBarLogoMaxWidth ? this.$experience.appBarLogoMaxWidth : '330px'))
                        : (this.$experience.appBarLogoMaxWidth ? this.$experience.appBarLogoMaxWidth : '330px')
            },
            getLogo() {
                const logo = (this.$i18n.locale == 'en' && this.$experience.logoEN != null) ? this.$experience.logoEN : this.$experience.logoDark;
                return logo || this.$experience.logo || 'Vault_Blanc.svg';
            },
            getSecondLogo() {
                return (this.$i18n.locale == 'en' && this.$experience.logoSecondLoginEN != null) ? this.$experience.logoSecondLoginEN : this.$experience.logoSecondLogin;
            },
            logoSecondLoginMaxWidth() {
                return this.$experience.logoSecondLoginMaxWidth;
            },
            logoSecondLoginMaxheight() {
                return this.$experience.logoSecondLoginMaxheight;
            }
        },
        methods: {
            // Register global keyboard shortcuts
            registerKeyboardShortcuts() {
                // Help dialog shortcut
                this.$registerShortcut(SHORTCUTS.HELP, () => {
                    this.showKeyboardHelp = true;
                }, this.$t('accessibility.keyboardShortcuts'));

                // Close modal/drawer with Escape
                this.$registerShortcut(SHORTCUTS.CLOSE, () => {
                    if (this.showKeyboardHelp) {
                        this.showKeyboardHelp = false;
                    } else if (this.showModal) {
                        this.showModal = false;
                    } else if (this.drawer) {
                        this.drawer = false;
                    }
                }, this.$t('accessibility.closeDialog'));
            },

            // Initialise le tableau de bord
            initDashboard() {
                let self = this;
                this.$axios.get('login/account-status')
                    .then(function (response) {
                        if (!response.data.success && response.data.error == 'session-expired') {
                            self.$router.push({ name: 'login' });
                        }
                        else if (response.data.accountStatus.MustSetPassword) {
                            self.MustSetPassword = response.data.accountStatus.MustSetPassword;
                            self.PasswordExpired = response.data.accountStatus.PasswordExpired;
                            self.SecurityModal();
                        }
                    })
                    .then(() => {
                        self.$axios.get('account/user-info')
                            .then(function (response) {
                                if (!response.data.success && response.data.error == 'session-expired') {
                                    self.$router.push({ name: 'login' });
                                }
                                self.userData = response.data;
                            })
                            .then(() => { self.loaded = true })
                    });
            },
            selectReport(reportCode) {
                let self = this;

                let isReportDisplayed = self
                    .selectedReportCodes
                    .indexOf(reportCode) > -1;

                if (!isReportDisplayed) {
                    self.selectedReportCodes.push(reportCode);
                }
            },
            unselectReport(reportCode) {
                let self = this,
                    selectedReportIndex = self.selectedReportCodes.indexOf(reportCode),
                    isSelected = selectedReportIndex > -1,
                    reportListEl = self.$el.querySelector('.arkivoj--report--list');

                if (isSelected) {
                    self.$delete(self.selectedReportCodes, selectedReportIndex);
                }

                reportListEl.scrollIntoView({ behavior: 'smooth' });
            },
            toggleLocale() {
                this.$i18n.locale = this.$i18n.locale == 'fr' ? 'en' : 'fr';
                localizationService.setLang(this.$i18n.locale);
            },
            setExperience() {
                let self = this;
                self.experienceLoaded = false;

                loginService.displayLogin()
                    .then((value => {
                        Vue.prototype.$hasonboarding = value.hasOnboarding;

                        let previousLanguage = Cookies.get('lang');
                        if (previousLanguage != null)
                            this.$i18n.locale = previousLanguage;
                        else if (value.defaultLanguage) {
                            this.$i18n.locale = value.defaultLanguage;

                        }

                        let experienceLower = value.experienceCode?.toLowerCase() ?? "sogica";
                        let tindex = brandingJson.findIndex(x => x.key === experienceLower);
                        if (tindex >= 0) {
                            Vue.prototype.$experience = Object.assign({}, brandingJson[tindex]);
                        } else
                            Vue.prototype.$experience = Object.assign({}, brandingJson[0]);
                    }))
                    .catch(() => {
                        Vue.prototype.$experience = Object.assign({}, brandingJson[0]);
                    })
                    .then(() => {
                        Cookies.set("exp", this.$experience.key);
                        const name = this.$experience.key;
                        const dark = this.$experience.theme.themes.dark;
                        const light = this.$experience.theme.themes.light;
                        // set themes
                        Object.keys(dark).forEach(key => {
                            this.$vuetify.theme.themes.dark[key] = dark[key];
                        });
                        Object.keys(light).forEach(key => {
                            this.$vuetify.theme.themes.light[key] = light[key];

                        });
                        // also save theme name to disable selection
                        this.$vuetify.theme.themes.name = name;

                        // set favicon
                        if (this.$experience.favicon) {
                            const faviconLink = document.querySelector('#favicon');
                            faviconLink.href = `./assets/Branding/${this.$experience.key}/Images/${this.$experience.favicon}`;
                            const appleTouchIconLink = document.querySelector('#apple-touch-icon');
                            appleTouchIconLink.href = faviconLink.href;
                        }
                        if (this.$experience.appTitle) {
                            const appTitle = document.querySelector('#title');
                            appTitle.innerHTML = this.$experience.appTitle;
                        }

                        this.experienceLoaded = true;
                    });

            },
            ProfileModal() {
                this.ShowUserInfo = true;
                this.ShowPasswordManager = true;
                this.MustSetPassword = false;
                this.isLegalModal = false;
                this.isProfileModal = true;
                this.showModal = true;
            },
            LegalModal() {
                this.ShowUserInfo = false;
                this.ShowPasswordManager = false;
                this.MustSetPassword = false;
                this.isLegalModal = true;
                this.isProfileModal = false;
                this.showModal = true;
            },
            SecurityModal() {
                this.ShowUserInfo = false;
                this.ShowPasswordManager = this.MustSetPassword;
                this.isLegalModal = false;
                this.isProfileModal = true;
                this.showModal = true;
            },
            CloseModalfunction() {
                this.showModal = false;
            },
            CloseOnBoardingModal() {
                this.showOnBoardingModal = false;
            },
            TestOnBoardingModal() {
                this.showOnBoardingModal = true;
            },
            Sort(byAttribute) {
                let self = this;

                // Remove state class(desc/asc) from previous sorted columns
                if (self.sortedAttribute != null && self.sortedAttribute != "") {
                    document.getElementById(self.sortedAttribute).classList.remove("asc");
                    document.getElementById(self.sortedAttribute).classList.remove("desc");
                }
                if (self.sortedAttribute == byAttribute && self.sortedWay == "asc") {
                    self.reportsInfos.sort(function (a, b) {
                        if (a[byAttribute] < b[byAttribute]) {
                            return 1;
                        } else if (a[byAttribute] > b[byAttribute]) {
                            return -1;
                        } else {
                            return 0;
                        }
                    });
                    document.getElementById(self.sortedAttribute).classList.add("desc");
                    self.sortedWay = "desc";
                } else if (self.sortedAttribute == byAttribute && self.sortedWay == "desc") {
                    self.reportsInfos.sort(function (a, b) {
                        if (a[byAttribute] < b[byAttribute]) {
                            return -1;
                        } else if (a[byAttribute] > b[byAttribute]) {
                            return 1;
                        } else {
                            return 0;
                        }

                    });
                    document.getElementById(self.sortedAttribute).classList.add("asc");
                    self.sortedWay = "asc";
                } else {
                    self.reportsInfos.sort(function (a, b) {
                        if (a[byAttribute] < b[byAttribute]) {
                            return -1;
                        } else if (a[byAttribute] > b[byAttribute]) {
                            return 1;
                        } else {
                            return 0;
                        }

                    });


                    document.getElementById(byAttribute).classList.add("asc");
                    self.sortedAttribute = byAttribute;
                    self.sortedWay = "asc";
                }

            },
            OnboardingClick() {
                this.showOnBoardingModal = true;
            },
            onLogout() {
                this.drawer = false;
                this.showLogout = false;
                this.$router.push("/login");
            },
            userDataUpdated(data) {
                this.userData = data;
            },
            formatMenuText(text) {
                if (!text) return text;

                // Check the titleCase setting from the current experience
                const titleCase = this.$experience.titleCase !== false; // default to true if not set

                if (titleCase) {
                    // Title case: capitalize first letter of all words
                    return text.split(' ').map(word => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        return word.charAt(0).toUpperCase() + word.slice(1);
                    }).join(' ');
                } else {
                    // Sentence case: only capitalize first letter of first word
                    // But preserve acronyms
                    const words = text.split(' ');
                    return words.map((word, index) => {
                        if (word.length === 0) return word;
                        // Check if word is an acronym (all uppercase)
                        if (word === word.toUpperCase() && word.length > 1) {
                            return word; // Keep acronyms as-is
                        }
                        if (index === 0) {
                            // First word: capitalize first letter
                            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
                        } else {
                            // Other words: all lowercase
                            return word.toLowerCase();
                        }
                    }).join(' ');
                }
            }

        },
    };
</script>

<style>
    html {
        overflow-y: auto !important;
    }

    .input-tel__input[data-v-e59be3b4] {
        font-size: 16px !important;
        height: 56px !important;
        min-height: 56px !important;
        padding-top: 0 !important;
        width: 100% !important;
        border-radius: 10px !important;
        background-color: transparent !important;
        border-color: rgba(0,0,0,0.4) !important;
        border-style: solid !important;
        border-width: 1px !important;
    }

    .input-tel__input[data-v-e59be3b4]:hover {
        border-color: rgba(0,0,0,0.8) !important;
    }

    .input-tel__label {
        font-size: 16px !important;
        transform: translateY(-14px) scale(0.75) !important;
        transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1) !important;
        background-color: white !important;
        padding: 0 2px !important;
        left: -2px !important;
    }

    .v-application {
        font-family: 'Open sans', 'Sans-serif' !important;
        letter-spacing: 0px !important;
        background-color: var(--background-color) !important;
    }

    .v-input__append-inner{
        margin: auto !important;
    }

    .v-btn,
    .v-card,
    .v-card-text,
    .v-card__title,
    .v-card__subtitle,
    .v-tab {
        letter-spacing: 0px !important;
    }

    .v-btn {
        font-weight: 600 !important;
    }

    .v-btn,
    .v-text-field {
        border-radius: 10px;
    }

    .v-btn--plain:not(.v-btn--active):not(.v-btn--loading):not(:focus):not(:hover) .v-btn__content {
        opacity: 1;
    }

    .v-toolbar__content {
        height: 64px !important;
    }

    .v-overlay.v-overlay--active {
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
        filter: blur(0);
    }

    .v-main {
        padding: 64px 0px 0px !important;
    }

    .font-weight-bold {
        font-weight: 700 !important;
    }

    .v-application .font-weight-medium {
        font-weight: 600 !important;
    }

    .v-select__selections {
        line-height: 20px !important;
    }

    /* for mobile */
    @media (max-width: 960px) {
        html {
            overflow-y: auto !important;
            overflow-x: hidden !important;
        }

        body {
            overflow: clip !important;
        }

        .v-toolbar__content {
            height: 56px !important;
        }

        .v-main {
            padding: 56px 0px 0px !important;
        }
    }

    /* Custom classes */
    .better-break {
        word-break: break-word;
    }

    .btn-avatar {
        background-color: white !important;
        color: var(--primary) !important;
        width: 48px !important;
        height: 48px !important;
        min-height: 0px !important;
        border-radius: 24px !important;
        opacity: 1 !important;
    }

        .btn-avatar:hover {
            background-color: var(--primary) !important;
            color: white !important;
            width: 48px !important;
            height: 48px !important;
            min-height: 0px !important;
            border-radius: 24px !important;
            opacity: 1 !important;
        }

    .btn-avatar-active {
        background-color: var(--primary) !important;
        color: white !important;
        width: 48px !important;
        height: 48px !important;
        min-height: 0px !important;
        border-radius: 24px !important;
        opacity: 1 !important;
    }

    .btn-nav {
        border-radius: 0px !important;
        opacity: 1 !important;
        height: 64px !important;
        color: var(--headerTextColor) !important;
    }

        .btn-nav:hover {
            border-radius: 0px !important;
            opacity: 1 !important;
            color: var(--navHoverFontColor) !important;
            background-color: var(--navHoverBackgroundColor) !important;
            height: 64px !important;
        }

        .btn-nav .v-btn__content {
            opacity: 1 !important;
        }

    .btn-nav-active {
        border-radius: 0px !important;
        opacity: 1 !important;
        color: var(--activeNavTextColor) !important;
        background-color: white !important;
        height: 64px !important;
    }

    .c-drawer-zindex {
        z-index: 99 !important;
    }

    .card-subtitle {
        font-family: Open sans, sans-serif !important;
        font-weight: normal !important;
        font-size: 20px !important;
        color: var(--titleColor) !important;
        opacity: 1 !important;
    }

    .card-title {
        font-family: Open sans, sans-serif !important;
        font-weight: normal !important;
        font-size: 25px !important;
        color: var(--titleColor) !important;
        opacity: 1 !important;
    }

    .circle {
        width: 36px;
        height: 36px;
        border-radius: 18px;
        background-color: white;
        margin: 4px;
    }

    .line-height-normal {
        line-height: 30px !important;
    }

    .login-bg {
        background-color: var(--login-background-color);
    }

    .min-height-1 {
        min-height: 56px !important;
    }

    .text-small {
        font-size: 14px !important;
    }

    .text-normal {
        font-size: 16px !important;
    }

    .text-large {
        font-size: 25px !important;
    }

    .text-xlarge {
        font-size: 35px !important;
    }

    .text-color-primary {
        color: var(--primary);
    }

    .opacity-1 {
        opacity: 1 !important;
    }

    /* for mobile */
    @media (max-width: 960px) {
        .card-subtitle {
            font-family: Open sans, sans-serif !important;
            font-weight: normal !important;
            font-size: 15px !important;
            color: var(--titleColor) !important;
            opacity: 1 !important;
        }

        .card-title {
            font-family: Open sans, sans-serif !important;
            font-weight: normal !important;
            font-size: 18px !important;
            color: var(--titleColor) !important;
            opacity: 1 !important;
        }

        .text-small {
            font-size: 12px !important;
        }

        .text-normal {
            font-size: 15px !important;
        }

        .text-large {
            font-size: 18px !important;
        }

        .text-xlarge {
            font-size: 25px !important;
        }
    }

    .cookie-consent {
        background-color: rgba(234,234,234,1);
        bottom: 0;
        color: black;
        position: fixed;
        width: 100%;
        padding: 1em;
        text-align: center;
    }

        .cookie-consent > section {
            display: inline;
        }

        .cookie-consent .cookie-consent-message {
            margin-right: 1em;
        }

        .cookie-consent .cookie-consent-link {
            margin-left: 1em;
        }

        .cookie-consent .cookie-consent-compliance {
            padding: 6px 12px;
            border: 1pt solid gray;
            transition: 0.2s ease-in all;
        }

            .cookie-consent .cookie-consent-compliance:hover {
                padding: 6px 12px;
                background-color: gray;
            }

    /* Fix footer link color contrast for WCAG AA compliance */
    .v-footer a.footerColor {
        color: #006891 !important;
    }

    .v-footer a.footerColor:hover {
        color: #005070 !important;
        text-decoration: underline;
    }
</style>
